import { ChangeDetectionStrategy, Component, OnInit, TrackByFunction } from '@angular/core'
import { ActivatedRoute, ParamMap, Router } from '@angular/router'
import { combineLatest, BehaviorSubject, distinctUntilChanged, map, switchMap, tap } from 'rxjs'
import { filter, shareReplay, withLatestFrom } from 'rxjs/operators'
import { AdComponentInputs } from 'src/app/shared/ads/ad/ad-component.interface'
import {
  AD_LEADERBOARD_LARGE_FLEX_SIZE,
  AD_LEADERBOARD_SMALL_SIZE,
  AD_RECTANGLE_LARGE_SIZE,
  AD_ADHESION_SIZE,
  AD_SKIN_SIZE
} from 'src/app/shared/ads/ads.utility'
import { AdConfig } from 'src/app/shared/ads/advertising-service/ad-config'
import { SegmentEvents } from 'src/app/shared/analytics/models/segment-events.model'
import { SegmentService } from 'src/app/shared/analytics/services/segment.service'
import {
  FEAT_OFP_DATA_SEARCH_ID,
  FEAT_OFP_DATA_SEARCH_VARIATION
} from 'src/app/shared/experimentation/experiment.model'
import { ExperimentationService } from 'src/app/shared/experimentation/experimentation.service'
import { notNullOrUndefined } from 'src/app/shared/functions/type-guards'
import { isStringNegativeNumber } from 'src/app/shared/utility-functions/string.utility'
import { trackById } from 'src/app/shared/utility-functions/track-by-id.utility'
import { SearchDataService } from 'src/app/singleton-services/search-data.service'
import { FloApiSearchResponseData } from 'src/app/singleton-services/search-data.service.interface'
import { environment } from 'src/environments/environment'
import { isSearchContentType, mapSearchMetaToPaginationModel, mapSearchExtraToTabs } from './old-search.utility'
import { OldSearchAnalyticsService } from './old-search-analytics.service'
import { SearchTab } from './old-search.interfaces'
import { VerticalService } from '../../../singleton-services/vertical.service'

/**
 * @deprecated
 * This component is deprecated and should not be used to support any future features.
 * Instead, use the new search SDUI search component.
 * @see SearchComponent
 */
@Component({
  selector: 'flo-old-search',
  templateUrl: './old-search.component.html',
  styleUrls: ['./old-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OldSearchComponent implements OnInit {
  // spinner logic
  private isContentLoadingSource = new BehaviorSubject<boolean>(false)
  public isContentLoading$ = this.isContentLoadingSource.asObservable()

  constructor(
    private readonly router: Router,
    private readonly ar: ActivatedRoute,
    private readonly searchDataService: SearchDataService,
    private readonly segmentService: SegmentService,
    private readonly searchAnalytics: OldSearchAnalyticsService,
    private readonly experimentationService: ExperimentationService,
    private readonly verticalService: VerticalService
  ) {}

  // CXP-5408: Feature Flag for OFP Search
  private ofpFeatureFlag$ = this.experimentationService.isInVariation(
    FEAT_OFP_DATA_SEARCH_ID,
    FEAT_OFP_DATA_SEARCH_VARIATION
  )

  ngOnInit() {
    // fire page call only once. User interactions (page, search query, tabs) will not fire page calls.
    this.searchAnalytics.sendPageCall()
  }

  private routeQueryParams$ = this.ar.queryParamMap.pipe(
    distinctUntilChanged(
      (x: ParamMap, y: ParamMap) =>
        x.get('q') === y.get('q') && x.get('page') === y.get('page') && x.get('type') === y.get('type')
    ),
    shareReplay(1)
  )
  public readonly siteId$ = this.verticalService.siteId$

  public readonly searchQueryParam$ = this.routeQueryParams$.pipe(
    map(queryParamsMap => queryParamsMap.get('q') ?? undefined),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public readonly typeQueryParam$ = this.routeQueryParams$.pipe(
    map(queryParamsMap => queryParamsMap.get('type') ?? undefined),
    map(type => (isSearchContentType(type) ? type : undefined)),
    distinctUntilChanged(),
    shareReplay(1)
  )

  public readonly pageQueryParam$ = this.routeQueryParams$.pipe(
    map(queryParamsMap => queryParamsMap.get('page') ?? undefined),
    map(page => {
      if (page === undefined) return page
      return isStringNegativeNumber(page) ? '1' : page
    }),
    distinctUntilChanged(),
    shareReplay(1)
  )

  private searchResponse$ = combineLatest([
    this.searchQueryParam$,
    this.typeQueryParam$,
    this.pageQueryParam$,
    this.ofpFeatureFlag$
  ]).pipe(
    tap(() => this.isContentLoadingSource.next(true)),
    switchMap(([searchParam, contentType, pageParam, isOfp]) => {
      return this.searchDataService.searchAll(searchParam, pageParam, contentType, isOfp)
    }),
    tap(() => this.isContentLoadingSource.next(false)),
    shareReplay(1)
  )

  // Use for tabs and pagination
  private responseMeta$ = this.searchResponse$.pipe(
    map(res => res.meta),
    shareReplay(1)
  )

  public pagination$ = this.responseMeta$.pipe(
    map(meta => mapSearchMetaToPaginationModel(meta, this.router, this.ar.snapshot.queryParams))
  )

  public searchTabs$ = this.responseMeta$.pipe(
    map(meta => meta.extra),
    withLatestFrom(this.typeQueryParam$),
    map(([extra, typeParam]) => mapSearchExtraToTabs(extra, typeParam)),
    shareReplay(1)
  )

  public searchLayout$ = combineLatest([this.searchResponse$, this.searchQueryParam$]).pipe(
    map(([res, query]) => {
      if (res.data === null) return []
      return res.data.map(d => ({ ...d, search_term: query }))
    }),
    shareReplay(1)
  )

  private searchResultsTracking$ = this.searchResponse$
    .pipe(filter(notNullOrUndefined), withLatestFrom(this.typeQueryParam$, this.searchQueryParam$))
    .subscribe(([response, typeQueryParams, searchQueryParams]) => {
      this.trackSearchResults(
        searchQueryParams,
        mapSearchExtraToTabs(response.meta.extra, typeQueryParams),
        response.data
      )
    })

  //  Handle User Inputs
  public updateSearchQueryParam(event: { searchTerm: string | null }) {
    const queryParams = { q: event.searchTerm, page: '1' }
    this.router.navigate([], {
      queryParams,
      relativeTo: this.ar,
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
  }

  // user clicks tab
  public updateTypeQueryParam(type: string) {
    const queryParams = { type: type.length > 0 ? type : undefined, page: '1' }
    this.router.navigate([], {
      queryParams,
      relativeTo: this.ar,
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
  }

  public trackById = trackById
  public trackByTab: TrackByFunction<SearchTab> = (i: number, item: SearchTab) => item.value
  /**
   * All props needed for display ad placements.
   */

  public readonly AdComponent = import('src/app/shared/ads/ad/ad.component').then(m => m.AdComponent)

  public readonly adLeaderboardLarge: AdComponentInputs = {
    config: {
      adConfig: AdConfig('search/728x90_flex', AD_LEADERBOARD_LARGE_FLEX_SIZE, 'search-flex-large'),
      additionalOpts: { deferClick: environment.advertising.preventClickTimeout }
    }
  }

  public readonly adLeaderboardSmall: AdComponentInputs = {
    config: {
      adConfig: AdConfig('search/728x90_flex', AD_LEADERBOARD_SMALL_SIZE, 'search-flex-small')
    }
  }

  public readonly adRightRail: AdComponentInputs = {
    config: {
      adConfig: AdConfig('search/300x250_1', AD_RECTANGLE_LARGE_SIZE, 'search-right-rail-rectangle')
    }
  }

  public readonly adAdhesion: AdComponentInputs = {
    config: {
      adConfig: AdConfig('search/adhesion', AD_ADHESION_SIZE, 'search-adhesion'),
      additionalOpts: { maxRefreshTime: 30000 }
    }
  }

  public readonly adSkinProps: AdComponentInputs = {
    config: {
      adConfig: AdConfig('search/atf', AD_SKIN_SIZE, 'search-ad-skin')
    }
  }

  trackSearchResults(
    searchTerm: string | undefined,
    searchTabs: readonly SearchTab[] | undefined,
    searchResponseData?: readonly FloApiSearchResponseData[] | null
  ): void {
    if (!searchTerm) return
    this.segmentService.trackEvent({
      name: SegmentEvents.SEARCH_RESULTS,
      properties: {
        search_term: searchTerm,
        search_tabs: searchTabs,
        results:
          searchResponseData?.map(item => ({ node_id: item.node.id, node_type: item.type, title: item.title })) ?? []
      }
    })
  }
}
